/**
 * Notification Service
 * 
 * Handles all notification and messaging API calls including:
 * - Message CRUD operations
 * - Message status management
 * - Push notifications
 * - WhatsApp messaging
 * - SMS and communication
 */

import api from './api';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface Message {
  _id?: string;
  content?: string;
  recipient?: string;
  sender?: string;
  [key: string]: any;
}

export interface MessageStatus {
  messageId: string;
  status: 'read' | 'unread' | 'archived';
  [key: string]: any;
}

// ============================================================================
// Message CRUD Operations
// ============================================================================

/**
 * API: GET /api/v2.0/getMessages
 * Auth: no
 * Gets all messages
 */
export async function getMessages(params?: any): Promise<any> {
  return api.get('/getMessages', { params });
}

/**
 * API: GET /api/v2.0/getMessagesApp
 * Auth: no
 * Gets messages for the app
 */
export async function getMessagesApp(params?: any): Promise<any> {
  return api.get('/getMessagesAppNew', { params });
}

/**
 * API: GET /api/v2.0/getMessagesAppNew
 * Auth: yes
 * Gets messages for the app (new version)
 */
export async function getMessagesAppNew(params?: any): Promise<any> {
  return api.get('/getMessagesAppNew', { params });
}

/**
 * API: POST /api/v2.0/createMessage
 * Auth: no
 * Creates a new message
 */
export async function createMessage(data: Message): Promise<any> {
  return api.post('/createMessage', data);
}

/**
 * API: POST /api/v2.0/sendMessage
 * Auth: no
 * Sends a message
 */
export async function sendMessage(data: Message): Promise<any> {
  return api.post('/sendMessage', data);
}

/**
 * API: POST /api/v2.0/sendMessageNew
 * Auth: no
 * Sends a message (new version)
 */
export async function sendMessageNew(data: Message): Promise<any> {
  return api.post('/sendMessageNew', data);
}

/**
 * API: POST /api/v2.0/sendMessageToMany
 * Auth: no
 * Sends a message to multiple recipients
 */
export async function sendMessageToMany(data: { recipients: string[]; message: string }): Promise<any> {
  return api.post('/sendMessageToMany', data);
}

// ============================================================================
// Message Status Management
// ============================================================================

/**
 * API: POST /api/v2.0/setMessageStatus
 * Auth: no
 * Sets the status of a message
 */
export async function setMessageStatus(data: MessageStatus): Promise<any> {
  return api.post('/setMessageStatusNew', { msg_id: data.messageId, status: data.status });
}

/**
 * API: POST /api/v2.0/setMessageStatusNew
 * Auth: yes
 * Sets the status of a message (new version)
 */
export async function setMessageStatusNew(data: MessageStatus): Promise<any> {
  return api.post('/setMessageStatusNew', { msg_id: data.messageId, status: data.status });
}

/**
 * API: POST /api/v2.0/receiveMessageStatus
 * Auth: no
 * Receives and processes message status updates
 */
export async function receiveMessageStatus(data: any): Promise<any> {
  return api.post('/receiveMessageStatus', data);
}

/**
 * API: POST /api/v2.0/setAllActionableMessageStatus
 * Auth: no
 * Sets status for all actionable messages
 */
export async function setAllActionableMessageStatus(data: { member_id?: string; status: string }): Promise<any> {
  return api.post('/setAllActionableMessageStatusNew', { status: data.status });
}

/**
 * API: POST /api/v2.0/setAllActionableMessageStatusNew
 * Auth: yes
 * Sets status for all actionable messages (new version)
 */
export async function setAllActionableMessageStatusNew(data: { status: string }): Promise<any> {
  return api.post('/setAllActionableMessageStatusNew', data);
}

// ============================================================================
// Push Notifications
// ============================================================================

/**
 * API: POST /api/v2.0/sendMessagePushNotification
 * Auth: no
 * Sends a push notification with a message
 */
export async function sendMessagePushNotification(data: { 
  title: string; 
  body: string; 
  recipients: string[] 
}): Promise<any> {
  return api.post('/sendMessagePushNotification', data);
}

// ============================================================================
// WhatsApp Integration
// ============================================================================

/**
 * API: GET /api/v2.0/isWhatsappSessionActive
 * Auth: no
 * Checks if WhatsApp session is active
 */
export async function isWhatsappSessionActive(): Promise<any> {
  return api.get('/isWhatsappSessionActive');
}

/**
 * API: POST /api/v2.0/sendWhatsAppTemplate
 * Auth: no
 * Sends a WhatsApp template message
 */
export async function sendWhatsAppTemplate(data: { 
  template: string; 
  recipients: string[];
  variables?: Record<string, string>
}): Promise<any> {
  return api.post('/sendWhatsAppTemplate', data);
}

/**
 * API: POST /api/v2.0/shouldSendTemplate
 * Auth: no
 * Checks if a WhatsApp template should be sent
 */
export async function shouldSendTemplate(data: { 
  templateId: string; 
  recipient: string 
}): Promise<any> {
  return api.post('/shouldSendTemplate', data);
}
